import { Injectable, computed, signal } from "@angular/core";

@Injectable({ providedIn: "root" })
export class MultiSelectionService {
  private readonly _selected = signal<Set<string>>(new Set());
  private readonly _excluded = signal<Set<string>>(new Set()); // Only used when "Select All" is active

  readonly selectedItems$ = this._selected.asReadonly();
  readonly excludedItems$ = this._excluded.asReadonly();

  readonly selectedList = computed(() => Array.from(this._selected()));
  readonly excludedList = computed(() => Array.from(this._excluded()));

  readonly count = computed(() => this._selected().size);
  readonly isEmpty = computed(() => this._selected().size === 0);
  readonly isNotEmpty = computed(() => this._selected().size > 0);
  readonly excludedCount = computed(() => this._excluded().size);

  private readonly _selectedRows = signal<Map<string, any>>(new Map());
  private readonly _excludedRows = signal<Map<string, any>>(new Map());

  readonly selectedRows$ = this._selectedRows.asReadonly();
  readonly excludedRows$ = this._excludedRows.asReadonly();

  readonly selectedRowsList = computed(() =>
    Array.from(this._selectedRows().values()),
  );
  readonly excludedRowsList = computed(() =>
    Array.from(this._excludedRows().values()),
  );

  private readonly _isAllSelected = signal<boolean>(false);
  readonly isAllSelected$ = this._isAllSelected.asReadonly();

  toggleSelection(itemId: string): void {
    if (this._isAllSelected()) {
      // In "select all" mode — track unchecked items in exclude list
      this._excluded.update((current) => {
        const next = new Set(current);
        next.has(itemId) ? next.delete(itemId) : next.add(itemId);
        return next;
      });
    } else {
      // Normal mode — track checked items in selected list
      this._selected.update((current) => {
        const next = new Set(current);
        next.has(itemId) ? next.delete(itemId) : next.add(itemId);
        return next;
      });
    }
  }

  selectAll(itemIds: string[]): void {
    this._selected.update((current) => new Set([...current, ...itemIds]));
  }

  deselectAll(itemIds: string[]): void {
    this._selected.update((current) => {
      const next = new Set(current);
      itemIds.forEach((id) => next.delete(id));
      return next;
    });
  }

  clear(): void {
    this._selected.set(new Set());
    this._excluded.set(new Set());
    this._selectedRows.set(new Map());
    this._excludedRows.set(new Map());
    this._isAllSelected.set(false);
  }

  isSelected(itemId: string): boolean {
    if (this._isAllSelected()) {
      return !this._excluded().has(itemId); // all selected except excluded
    }
    return this._selected().has(itemId);
  }

  getSelectedItems(): string[] {
    return Array.from(this._selected());
  }

  getExcludedItems(): string[] {
    return Array.from(this._excluded());
  }

  toggleSelectAll(): void {
    this._isAllSelected.update((current) => !current);

    if (!this._isAllSelected()) {
      this.clear();
    }
  }

  applySelectAllToPage(pageItemIds: string[]): void {
    if (this._isAllSelected()) {
      const excluded = this._excluded();
      // Filter out already excluded items before selecting
      const itemsToSelect = pageItemIds.filter((id) => !excluded.has(id));
      this.selectAll(itemsToSelect);
    }
  }

  toggleSelectionRow(row: any): void {
    this.toggleSelection(row.id);

    if (this._isAllSelected()) {
      // In "select all" mode — track unchecked rows in exclude map
      this._excludedRows.update((current) => {
        const next = new Map(current);
        next.has(row.id) ? next.delete(row.id) : next.set(row.id, row);
        return next;
      });
    } else {
      // Normal mode — track checked rows in selected map
      this._selectedRows.update((current) => {
        const next = new Map(current);
        next.has(row.id) ? next.delete(row.id) : next.set(row.id, row);
        return next;
      });
    }
  }

  getSelectedRows(): any[] {
    return Array.from(this._selectedRows().values());
  }

  getExcludedRows(): any[] {
    return Array.from(this._excludedRows().values());
  }

  selectAllRows(rows: any[]): void {
    this._selectedRows.update((current) => {
      const next = new Map(current);
      rows.forEach((row) => next.set(row.id, row));
      return next;
    });

    // Keep the id set in sync so UI checks (isSelected) reflect selection
    this._selected.update((current) => {
      const next = new Set(current);
      rows.forEach((row) => next.add(row.id));
      return next;
    });
  }

  deselectAllRows(rows: any[]): void {
    this._selectedRows.update((current) => {
      const next = new Map(current);
      rows.forEach((row) => next.delete(row.id));
      return next;
    });

    // Keep the id set in sync when rows are deselected
    this._selected.update((current) => {
      const next = new Set(current);
      rows.forEach((row) => next.delete(row.id));
      return next;
    });
  }

  applySelectAllToPageRows(pageRows: any[]): void {
    if (this._isAllSelected()) {
      const excludedRows = this._excludedRows();
      // Filter out already excluded rows before selecting
      const rowsToSelect = pageRows.filter((row) => !excludedRows.has(row.id));
      this.selectAllRows(rowsToSelect);
    }
  }
}
